import * as THREE from 'three';
import type { GroundRoadTexturesDescriptor } from './GroundRoadTextures.js';
import type { GroundTargetTexturesDescriptor } from './GroundTargetTextures.js';

export class GroundShader {
    private static readonly GROUND_SHADER_LAMBERT_LIGHT_GAIN = 1.5;
    private static readonly OVERLAY_GLSL = `
            // =====================================
            // GEOMETRY HELPERS
            // =====================================

            // Computes shortest distance from point p to segment ab:
            float sdSegment(vec2 p, vec2 a, vec2 b) {
                vec2 pa = p - a;
                vec2 ba = b - a;
                float h = clamp(dot(pa, ba) / (dot(ba, ba) + 1e-6), 0.0, 1.0);
                return length(pa - ba * h);
            }

            // Tests if point p lies inside triangle abc using edge signs:
            bool pointInTriangle(vec2 p, vec2 a, vec2 b, vec2 c) {
                float e0 = (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
                float e1 = (p.x - b.x) * (c.y - b.y) - (p.y - b.y) * (c.x - b.x);
                float e2 = (p.x - c.x) * (a.y - c.y) - (p.y - c.y) * (a.x - c.x);
                float eps = 1e-5;
                float minE = min(e0, min(e1, e2));
                float maxE = max(e0, max(e1, e2));
                return !(minE < -eps && maxE > eps);
            }

            // Converts a linear triangle index into 2D texel coordinates:
            ivec2 triangleTexCoord(int triangleIndex, ivec2 texSize) {
                return ivec2(triangleIndex % texSize.x, triangleIndex / texSize.x);
            }
            ivec2 linearTexCoord(int linearIndex, ivec2 texSize) {
                return ivec2(linearIndex % texSize.x, linearIndex / texSize.x);
            }

            
            // =====================================
            // TARGET LOGIC
            // =====================================

            // Resolves target kind at point p using adaptive-grid candidate triangles:
            // 1) Reject samples outside target world bounds.
            // 2) Resolve grid cell and fetch packed (start,count) span.
            // 3) Iterate candidate triangle refs and run point-in-triangle test.
            // Returns:
            // - x: target kind (0 when outside).
            // - y: minimum distance to the triangle edge (in local target-space units, for Anti-Aliasing Strokes later).
            vec2 sampleTargetKindAndEdgeDistance(vec2 p) {
                if (hasTargetTextures == 0 || showTargetOverlays == 0) return vec2(0.0, -1.0);
                vec2 targetRel = (p - targetWorldMin) / targetWorldSize;
                if (!(all(greaterThanEqual(targetRel, vec2(0.0))) && all(lessThan(targetRel, vec2(1.0))))) {
                    return vec2(0.0, -1.0);
                }
                vec2 pointLocal = p - targetWorldMin;

                // 2) Fetch the current adaptive-grid cell span.
                ivec2 cell = ivec2(
                    clamp(int(floor(targetRel.x * float(targetGridSize))), 0, targetGridSize - 1),
                    clamp(int(floor(targetRel.y * float(targetGridSize))), 0, targetGridSize - 1)
                );
                vec2 cellSpan = texelFetch(targetCellSpanTex, cell, 0).rg;
                int spanStart = int(cellSpan.x + 0.5);
                int spanCount = int(cellSpan.y + 0.5);

                // 3) Scan candidate refs in that cell and test exact triangle containment.
                ivec2 triTexSize = textureSize(targetTriangleABTex, 0);
                ivec2 refTexSize = textureSize(targetCellTriangleTex, 0);
                for (int i = 0; i < 64; i++) {
                    if (i >= spanCount || i >= targetMaxTrianglesPerCell) break;
                    int refIndex = spanStart + i;
                    int triIndex = int(texelFetch(targetCellTriangleTex, linearTexCoord(refIndex, refTexSize), 0).r + 0.5);
                    if (triIndex < 0) break;

                    ivec2 triCoord = triangleTexCoord(triIndex, triTexSize);
                    vec4 triAB = texelFetch(targetTriangleABTex, triCoord, 0);
                    vec4 triCK = texelFetch(targetTriangleCKindTex, triCoord, 0);
                    if (pointInTriangle(pointLocal, triAB.xy, triAB.zw, triCK.xy)) {
                        float edgeDist = min(
                            sdSegment(pointLocal, triAB.xy, triAB.zw),
                            min(sdSegment(pointLocal, triAB.zw, triCK.xy), sdSegment(pointLocal, triCK.xy, triAB.xy))
                        );
                        return vec2(float(int(triCK.z + 0.5)), edgeDist);
                    }
                }

                return vec2(0.0, -1.0);
            }

            // =====================================
            // ROAD LOGIC
            // =====================================

            // Resolves nearest road distance and active half-width at point p.
            vec4 sampleRoadDistanceHalfWidthAndKind(vec2 p) {
                float minDist = 10000.0;
                float minHalfWidth = lineHalfWidth;
                int minLineKind = 0;
                int minSegIndex = -1;
                if (hasLineTextures == 1 && (showRoadOverlays == 1 || showAnalysisBorderOverlays == 1 || showLegalBorderOverlays == 1 || showBuildableBorderOverlays == 1)) {
                    vec2 rel = (p - lineWorldMin) / lineWorldSize;
                    ivec2 cell = ivec2(clamp(floor(rel * float(lineGridSize)), vec2(0.0), vec2(float(lineGridSize - 1))));

                    for (int i = 0; i < 64; i++) {
                        if (i >= maxSegmentsPerCell) break;
                        ivec2 fetchCoord = ivec2(i, cell.y * lineGridSize + cell.x);
                        int segIndex = int(texelFetch(cellSegmentTex, fetchCoord, 0).r);
                        if (segIndex < 0) break;

                        ivec2 texSize = textureSize(lineSegmentsTex, 0);
                        ivec2 segCoord = ivec2(segIndex % texSize.x, segIndex / texSize.x);
                        int lineKind = int(texelFetch(lineKindsTex, segCoord, 0).r + 0.5);
                        if (lineKind == 1 && showRoadOverlays == 0) continue;
                        if (lineKind == 2 && showAnalysisBorderOverlays == 0) continue;
                        if (lineKind == 3 && showLegalBorderOverlays == 0) continue;
                        if (lineKind == 4 && showBuildableBorderOverlays == 0) continue;
                        vec4 seg = texelFetch(lineSegmentsTex, segCoord, 0);
                        float segHalfWidth = texelFetch(lineWidthsTex, segCoord, 0).r;
                        if (segHalfWidth <= 0.0) segHalfWidth = lineHalfWidth;

                        float d = sdSegment(p, seg.xy, seg.zw);
                        if (d < minDist) {
                            minDist = d;
                            minHalfWidth = segHalfWidth;
                            minLineKind = lineKind;
                            minSegIndex = segIndex;
                        }
                    }
                }
                return vec4(minDist, minHalfWidth, float(minLineKind), float(minSegIndex));
            }
    `;

    createGroundShaderMaterial(
        shadingModel: 'basic' | 'lambert',
        fillColor: number,
        strokeFallback: number,
        analysisBorderColorHex: number,
        legalBorderColorHex: number,
        buildableBorderColorHex: number,
        roadTextures: GroundRoadTexturesDescriptor | null,
        targetTextures: GroundTargetTexturesDescriptor | null,
        targetColorsHex: number[],
        targetStrokeColorsHex: number[],
        showRoads: boolean,
        showAnalysisBorder: boolean,
        showLegalBorder: boolean,
        showBuildableBorder: boolean,
        showTargets: boolean,
        enableTargetEdgeAA: boolean,
        enableLambertLighting = true,
        renderBaseFill = true
    ): THREE.MeshBasicMaterial | THREE.MeshLambertMaterial | null {
        if (!roadTextures && !targetTextures) return null;

        const lineSegmentsTex = roadTextures?.texture ?? this.createPlaceholderTexture(4);
        const lineWidthsTex = roadTextures?.lineWidthsTexture ?? this.createPlaceholderTexture(1, THREE.RedFormat);
        const lineColorsTex = roadTextures?.lineColorsTexture ?? this.createPlaceholderTexture(4);
        const lineKindsTex = roadTextures?.lineKindsTexture ?? this.createPlaceholderTexture(1, THREE.RedFormat, 1);
        const cellSegmentTex = roadTextures?.cellTexture ?? this.createPlaceholderTexture(1, THREE.RedFormat, -1);
        const lineGridSize = roadTextures?.gridSize ?? 1;
        const lineWorldMin = roadTextures?.worldMin ?? new THREE.Vector2();
        const lineWorldSize = roadTextures?.worldSize ?? new THREE.Vector2(1, 1);
        const lineHalfWidth = roadTextures?.defaultHalfWidth ?? 0.5;
        const maxSegmentsPerCell = roadTextures?.maxSegmentsPerCell ?? 1;

        const targetTriangleABTex = targetTextures?.triangleABTexture ?? this.createPlaceholderTexture(4);
        const targetTriangleCKindTex = targetTextures?.triangleCKindTexture ?? this.createPlaceholderTexture(4);
        const targetCellSpanTex = targetTextures?.cellSpanTexture ?? this.createPlaceholderTexture(2, THREE.RGFormat);
        const targetCellTriangleTex = targetTextures?.cellTriangleTexture ?? this.createPlaceholderTexture(1, THREE.RedFormat, -1);
        const targetWorldMin = targetTextures?.worldMin ?? new THREE.Vector2();
        const targetWorldSize = targetTextures?.worldSize ?? new THREE.Vector2(1, 1);
        const targetGridSize = targetTextures?.gridSize ?? 1;
        const targetMaxTrianglesPerCell = targetTextures?.maxTrianglesPerCell ?? 1;

        const targetColors = targetColorsHex.map((hex) => new THREE.Color(hex));
        const targetStrokeColors = targetStrokeColorsHex.map((hex) => new THREE.Color(hex));
        const material =
            shadingModel === 'lambert'
                ? new THREE.MeshLambertMaterial({
                      color: fillColor,
                      transparent: false,
                      depthTest: true,
                      depthWrite: true,
                      side: THREE.FrontSide,
                  })
                : new THREE.MeshBasicMaterial({
                      color: fillColor,
                      transparent: false,
                      depthTest: true,
                      depthWrite: true,
                      side: THREE.FrontSide,
                  });

        const overlayUniforms: Record<string, { value: any }> = {
            strokeColor: { value: new THREE.Color(strokeFallback) },
            analysisBorderColor: { value: new THREE.Color(analysisBorderColorHex) },
            legalBorderColor: { value: new THREE.Color(legalBorderColorHex) },
            buildableBorderColor: { value: new THREE.Color(buildableBorderColorHex) },
            lineSegmentsTex: { value: lineSegmentsTex },
            lineWidthsTex: { value: lineWidthsTex },
            lineColorsTex: { value: lineColorsTex },
            lineKindsTex: { value: lineKindsTex },
            cellSegmentTex: { value: cellSegmentTex },
            lineHalfWidth: { value: lineHalfWidth },
            lineGridSize: { value: lineGridSize },
            lineWorldMin: { value: lineWorldMin },
            lineWorldSize: { value: lineWorldSize },
            maxSegmentsPerCell: { value: maxSegmentsPerCell },
            hasLineTextures: { value: roadTextures ? 1 : 0 },
            showRoadOverlays: { value: showRoads ? 1 : 0 },
            showAnalysisBorderOverlays: { value: showAnalysisBorder ? 1 : 0 },
            showLegalBorderOverlays: { value: showLegalBorder ? 1 : 0 },
            showBuildableBorderOverlays: { value: showBuildableBorder ? 1 : 0 },

            targetTriangleABTex: { value: targetTriangleABTex },
            targetTriangleCKindTex: { value: targetTriangleCKindTex },
            targetCellSpanTex: { value: targetCellSpanTex },
            targetCellTriangleTex: { value: targetCellTriangleTex },
            targetWorldMin: { value: targetWorldMin },
            targetWorldSize: { value: targetWorldSize },
            targetGridSize: { value: targetGridSize },
            targetMaxTrianglesPerCell: { value: targetMaxTrianglesPerCell },
            hasTargetTextures: { value: targetTextures ? 1 : 0 },
            showTargetOverlays: { value: showTargets ? 1 : 0 },
            targetColors: { value: targetColors },
            targetStrokeColors: { value: targetStrokeColors },
            enableTargetEdgeAA: { value: enableTargetEdgeAA ? 1 : 0 },
            renderBaseFill: { value: renderBaseFill ? 1 : 0 },
        };
        material.userData.overlayUniforms = overlayUniforms;

        material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, overlayUniforms);
            material.userData.overlayShader = shader;

            shader.vertexShader = shader.vertexShader
                .replace('#include <common>', '#include <common>\nvarying vec3 vModelPosition;')
                .replace('#include <begin_vertex>', '#include <begin_vertex>\nvModelPosition = position;');

            const overlayHeader = `
                varying vec3 vModelPosition;
                uniform vec3 strokeColor;
                uniform vec3 analysisBorderColor;
                uniform vec3 legalBorderColor;
                uniform vec3 buildableBorderColor;
                uniform sampler2D lineSegmentsTex;
                uniform sampler2D lineWidthsTex;
                uniform sampler2D lineColorsTex;
                uniform sampler2D lineKindsTex;
                uniform sampler2D cellSegmentTex;
                uniform float lineHalfWidth;
                uniform int lineGridSize;
                uniform vec2 lineWorldMin;
                uniform vec2 lineWorldSize;
                uniform int maxSegmentsPerCell;
                uniform int hasLineTextures;
                uniform int showRoadOverlays;
                uniform int showAnalysisBorderOverlays;
                uniform int showLegalBorderOverlays;
                uniform int showBuildableBorderOverlays;

                uniform sampler2D targetTriangleABTex;
                uniform sampler2D targetTriangleCKindTex;
                uniform sampler2D targetCellSpanTex;
                uniform sampler2D targetCellTriangleTex;
                uniform vec2 targetWorldMin;
                uniform vec2 targetWorldSize;
                uniform int targetGridSize;
                uniform int targetMaxTrianglesPerCell;
                uniform int hasTargetTextures;
                uniform int showTargetOverlays;
                uniform vec3 targetColors[5];
                uniform vec3 targetStrokeColors[5];
                uniform int enableTargetEdgeAA;
                uniform int renderBaseFill;
                ${GroundShader.OVERLAY_GLSL}
            `;

            shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>\n${overlayHeader}`);
            shader.fragmentShader = shader.fragmentShader.replace(
                'vec4 diffuseColor = vec4( diffuse, opacity );',
                `
                // =====================================
                // FRAGMENT MAIN LOGIC
                // =====================================

                // 1) Initialize base fill, if disabled it's full transparent:
                vec4 diffuseColor = renderBaseFill == 1 ? vec4(diffuse, 1.0) : vec4(0.0, 0.0, 0.0, 0.0);
                
                // 2) Project current fragment position to ground 2D space:
                vec2 point = vec2(vModelPosition.x, -vModelPosition.y);

                // 3) Get target kind + local edge-distance for this fragment:
                vec2 targetSample = sampleTargetKindAndEdgeDistance(point);
                int targetKind = int(targetSample.x + 0.5);
                
                // 3.1) Render Targets: Fill & Strokes with AA:  
                if (targetKind > 0 && targetKind < 5) {
                    // Fill: 
                    float targetAA = max(0.008, 0.4 * (fwidth(point.x) + fwidth(point.y)));
                    float targetCoverage = enableTargetEdgeAA == 1 ? smoothstep(0.0, targetAA, targetSample.y) : 1.0;
                    vec3 targetColor = targetColors[targetKind];

                    // Strokes: 
                    vec3 targetStrokeColor = targetStrokeColors[targetKind];
                    vec3 antiAliasedTargetColor = mix(targetStrokeColor, targetColor, targetCoverage);
                    diffuseColor.rgb = antiAliasedTargetColor;
                    diffuseColor.a = 1.0;
                }

                // 4) Roads. Sample nearest road/border segment distance:
                vec4 roadSample = sampleRoadDistanceHalfWidthAndKind(point);
                float minDist = roadSample.x;
                float minHalfWidth = roadSample.y;
                int roadKind = int(roadSample.z + 0.5);
                int roadSegIndex = int(roadSample.w + 0.5);
                
                // 4.1) Apply road-like textures only when a segment is inside of this fragment:
                if (hasLineTextures == 1 && minDist < minHalfWidth) {
                    
                    // a) Build anti-aliased edge blend factor:
                    float aa =  0.1;
                    float t = 1.0 - smoothstep(minHalfWidth - aa, minHalfWidth + aa * 0.25, minDist);
                    
                    // b) Select fallback overlay color by semantic line kind.
                    vec3 overlayColor = strokeColor;
                    if (roadKind == 2) {
                        overlayColor = analysisBorderColor;
                    } else if (roadKind == 3) {
                        overlayColor = legalBorderColor;
                    } else if (roadKind == 4) {
                        overlayColor = buildableBorderColor;
                    }
                    
                    // c) Override with per-segment color when available.
                    if (roadSegIndex >= 0) {
                        ivec2 colorTexSize = textureSize(lineColorsTex, 0);
                        ivec2 colorCoord = ivec2(roadSegIndex % colorTexSize.x, roadSegIndex / colorTexSize.x);
                        vec4 sampledRoadColor = texelFetch(lineColorsTex, colorCoord, 0);
                        if (sampledRoadColor.a > 0.0) {
                            overlayColor = sampledRoadColor.rgb;
                        }
                    }
                    
                    // d) Blend overlay over base fill (or write alpha-only when base is transparent).
                    if (diffuseColor.a <= 0.0) {
                        diffuseColor = vec4(overlayColor, t);
                    } else {
                        diffuseColor.rgb = mix(diffuseColor.rgb, overlayColor, t);
                        diffuseColor.a = max(diffuseColor.a, t);
                    }
                }
                
                // 5) In texture-only mode (Roads  separate texture), discard fragments that remain effectively transparent:
                if (renderBaseFill == 0 && diffuseColor.a <= 0.0001) discard;
                `
            );

            if (shadingModel === 'lambert' && !enableLambertLighting) {
                shader.fragmentShader = shader.fragmentShader
                    .replace('#include <lights_lambert_fragment>', 'vec3 outgoingLight = diffuseColor.rgb;')
                    .replace('#include <lights_fragment_begin>', '');
            } else if (shadingModel === 'lambert') {
                shader.fragmentShader = shader.fragmentShader.replace(
                    '#include <dithering_fragment>',
                    `outgoingLight *= ${GroundShader.GROUND_SHADER_LAMBERT_LIGHT_GAIN.toFixed(2)};\n#include <dithering_fragment>`
                );
            }
        };

        material.needsUpdate = true;
        return material;
    }

    private createPlaceholderTexture(
        channels: number,
        format: THREE.PixelFormat = THREE.RGBAFormat,
        initialValue: number = 0
    ): THREE.DataTexture {
        const data = new Float32Array(Math.max(channels, 1)).fill(initialValue);
        const texture = new THREE.DataTexture(data, 1, 1, format, THREE.FloatType);
        if (format === THREE.RedFormat) {
            (texture as any).internalFormat = 'R32F';
        }
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        return texture;
    }
}
